/**
 * Colyseus synchronised state.
 *
 * Defined once here and imported by both sides: the server mutates these, the
 * client reads them. Using the decorator-free `schema()` API keeps the build
 * simple (no experimentalDecorators, no esbuild decorator edge cases) while
 * still giving full type inference through `SchemaType`.
 *
 * Note what is *absent*: no trees, no terrain, no item positions. All of that
 * is regenerated from `seed` on both sides. Only genuinely dynamic state
 * travels over the wire.
 */

import { schema, type SchemaType } from '@colyseus/schema';

export const PlayerState = schema(
  {
    sessionId: 'string',
    name: 'string',

    // Transform. Clients own their own; the server sanity-checks it.
    x: 'float32',
    y: 'float32',
    z: 'float32',
    yaw: 'float32',
    pitch: 'float32',

    // See protocol.ts for the enum values.
    life: 'uint8',
    stance: 'uint8',

    health: 'uint8',
    stamina: 'uint8',
    breath: 'uint8',

    flashlightOn: 'boolean',
    battery: 'uint16',
    /** Radio scanner switched on. Requires a radio in the bag. */
    radioOn: 'boolean',

    /** Item kind currently held, or '' for empty-handed. */
    heldItem: 'string',
    inventory: { array: 'string' },

    /** -1 when not hiding / not climbing. */
    hidingSpotId: 'int16',
    towerId: 'int8',
    /** Height up the ladder, 0..1. */
    climbProgress: 'float32',

    /** Current continuous noise emission — drives the monster's hearing. */
    noise: 'float32',

    /** True during a forced confrontation; movement input is rejected. */
    frozen: 'boolean',

    connected: 'boolean',
    ready: 'boolean',
  },
  'PlayerState',
);
export type PlayerState = SchemaType<typeof PlayerState>;

export const MonsterStateSchema = schema(
  {
    x: 'float32',
    y: 'float32',
    z: 'float32',
    yaw: 'float32',
    /** See MonsterState in protocol.ts. */
    fsm: 'uint8',
    alertness: 'uint8',
    speed: 'float32',
    /** Session id of the current target, or '' when not pursuing anyone. */
    target: 'string',
    /** Visible so clients can render footfall dust and shake in sync. */
    stepPhase: 'float32',
  },
  'MonsterState',
);
export type MonsterStateSchema = SchemaType<typeof MonsterStateSchema>;

export const TowerState = schema(
  {
    id: 'uint8',
    activated: 'boolean',
    /** 0..1 transmit progress. */
    progress: 'float32',
    /** Session id currently transmitting, or ''. */
    transmittingBy: 'string',
    /** Where the signal currently sits on the dial, 0..1. */
    signalTarget: 'float32',
  },
  'TowerState',
);
export type TowerState = SchemaType<typeof TowerState>;

/** A burning flare on the ground: a light source and a lure. */
export const FlareState = schema(
  {
    id: 'uint16',
    x: 'float32',
    y: 'float32',
    z: 'float32',
    /** Seconds of burn remaining. */
    remaining: 'float32',
  },
  'FlareState',
);
export type FlareState = SchemaType<typeof FlareState>;

export const GameState = schema(
  {
    /** Everything spatial is derived from this. */
    seed: 'uint32',
    /** See Difficulty in difficulty.ts. Part of the world's identity. */
    difficulty: 'uint8',
    code: 'string',

    phase: 'uint8',
    elapsed: 'float32',

    players: { map: PlayerState },
    monster: MonsterStateSchema,
    towers: { array: TowerState },
    flares: { map: FlareState },

    /** Append-only list of collected item ids. */
    takenItems: { array: 'uint16' },
    /** Append-only list of unlocked clue indices. */
    clues: { array: 'uint8' },

    towersActivated: 'uint8',
    towersRequired: 'uint8',

    /** Seconds until the helicopter arrives, once rescue is called. */
    rescueTimer: 'float32',
    /** Seconds held inside the extraction zone. */
    extractionHold: 'float32',

    hostSessionId: 'string',
  },
  'GameState',
);
export type GameState = SchemaType<typeof GameState>;
